import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { AnimatedShinyText } from "@/components/ui/magicui/animated-shiny-text";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight } from "lucide-react";
import PropTypes from "prop-types";
import { memo, useCallback, useState } from "react";
import Markdown from "../markdown/Markdown";

/**
 * ReasoningBlock - Displays AI reasoning/thinking content in a collapsible format
 * Follows OpenAI's design patterns for reasoning display
 */
const ReasoningBlock = memo(
  ({
    content,
    duration = null,
    isStreaming = false,
    className,
    defaultOpen = false,
    messageId, // Added for preference persistence
  }) => {
    // Get preference key for this reasoning block
    const preferenceKey = messageId ? `reasoning-block-${messageId}` : null;

    // Initialize state from localStorage preference or default to closed
    const [isOpen, setIsOpen] = useState(() => {
      if (!preferenceKey) return defaultOpen;
      const saved = localStorage.getItem(preferenceKey);
      return saved !== null ? saved === "true" : defaultOpen;
    });

    // Track if user has manually interacted with this block
    const [hasUserInteraction, setHasUserInteraction] = useState(() => {
      if (!preferenceKey) return false;
      return localStorage.getItem(preferenceKey) !== null;
    });

    // Handle user toggle with preference persistence
    const handleToggle = useCallback(
      (newState) => {
        setIsOpen(newState);
        setHasUserInteraction(true);
        if (preferenceKey) {
          localStorage.setItem(preferenceKey, String(newState));
        }
      },
      [preferenceKey]
    );

    // Show the block if streaming even without content, or if we have content, or if we have duration
    if ((!content || content.trim() === "") && !isStreaming && !duration) {
      return null;
    }

    // ADDED: Validate that content is actually reasoning content
    // Filter out content that looks like regular response content
    const looksLikeReasoningContent = (text) => {
      if (!text || typeof text !== "string") return false;

      // If it's very short, it might be reasoning
      if (text.length < 100) return true;

      // Check for reasoning indicators
      const reasoningIndicators = [
        "thinking",
        "analyze",
        "consider",
        "approach",
        "strategy",
        "reasoning",
        "logic",
        "step",
        "process",
        "evaluate",
        "I need to",
        "Let me",
        "First",
        "Then",
        "Therefore",
      ];

      const hasReasoningIndicators = reasoningIndicators.some((indicator) =>
        text.toLowerCase().includes(indicator.toLowerCase())
      );

      // Check for non-reasoning indicators (response content)
      const responseIndicators = [
        "Here is",
        "Here are",
        "Based on",
        "According to",
        "The answer",
        "To solve",
        "You can",
        "I recommend",
        "The result",
        "In conclusion",
      ];

      const hasResponseIndicators = responseIndicators.some((indicator) =>
        text.toLowerCase().includes(indicator.toLowerCase())
      );

      // If it has response indicators, it's likely not reasoning
      if (hasResponseIndicators && !hasReasoningIndicators) {
        console.warn(
          `🚫 [REASONING-FILTER] Content looks like response, not reasoning:`,
          {
            contentPreview: text.substring(0, 100) + "...",
            hasReasoningIndicators,
            hasResponseIndicators,
          }
        );
        return false;
      }

      return true;
    };

    // Clean up the content by removing any remaining XML tags
    const cleanContent = content
      ? content
          .replace(/<thinking>/g, "")
          .replace(/<\/thinking>/g, "")
          .replace(/<think>/g, "")
          .replace(/<\/think>/g, "")
          .trim()
      : "";

    // ADDED: Filter out content that doesn't look like reasoning
    if (
      cleanContent &&
      !isStreaming &&
      !looksLikeReasoningContent(cleanContent)
    ) {
      console.warn(
        `🚫 [REASONING-BLOCK] Filtering out non-reasoning content from reasoning block`
      );
      return null;
    }

    // Format duration display with singular/plural
    const formatDuration = (seconds) => {
      if (!seconds) return "";
      if (seconds < 1) return "less than 1 second";
      const rounded = Math.round(seconds);
      if (rounded === 1) return "1 second";
      return `${rounded} seconds`;
    };

    const durationText = duration ? formatDuration(duration) : "";

    return (
      <div
        className={cn(
          "reasoning-block mb-4 overflow-hidden transition-all duration-300 ease-in-out",
          className
        )}
      >
        <Collapsible open={isOpen} onOpenChange={handleToggle}>
          <CollapsibleTrigger className="w-full bg-transparent border-none outline-none focus:outline-none hover:bg-transparent">
            <div className="flex items-center gap-2 hover:bg-muted/10 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center gap-2 text-sm">
                {isStreaming ? (
                  <AnimatedShinyText
                    className="text-sm font-medium mx-0 max-w-none"
                    shimmerWidth={100}
                  >
                    Thinking...
                  </AnimatedShinyText>
                ) : (
                  <span className="font-medium text-muted-foreground">
                    Thought{durationText ? ` for ${durationText}` : ""}
                  </span>
                )}
              </div>
              <div className="flex items-center text-muted-foreground transition-transform duration-200">
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                ) : (
                  <ChevronRight className="h-4 w-4 transition-transform duration-200" />
                )}
              </div>
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="overflow-hidden transition-all duration-300 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-up-1 data-[state=open]:slide-down-1">
            <div className="border-t border-border/30 bg-transparent">
              <div className="px-4 text-sm">
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <div className="text-muted-foreground">
                    {cleanContent ? (
                      <Markdown darkMode={true} className="reasoning-content">
                        {cleanContent}
                      </Markdown>
                    ) : isStreaming ? (
                      <div className="text-muted-foreground italic">
                        Thinking in progress...
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  },
  // Custom memo comparison to ensure re-renders when streaming state changes
  (prevProps, nextProps) => {
    // Always re-render if streaming state changes
    if (prevProps.isStreaming !== nextProps.isStreaming) {
      return false;
    }

    // Re-render if content changes
    if (prevProps.content !== nextProps.content) {
      return false;
    }

    // Re-render if duration changes (completion indicator)
    if (prevProps.duration !== nextProps.duration) {
      return false;
    }

    // Re-render if className changes
    if (prevProps.className !== nextProps.className) {
      return false;
    }

    // Re-render if defaultOpen changes
    if (prevProps.defaultOpen !== nextProps.defaultOpen) {
      return false;
    }

    // Otherwise, don't re-render
    return true;
  }
);

ReasoningBlock.displayName = "ReasoningBlock";

ReasoningBlock.propTypes = {
  content: PropTypes.string.isRequired,
  duration: PropTypes.number,
  isStreaming: PropTypes.bool,
  className: PropTypes.string,
  defaultOpen: PropTypes.bool,
  messageId: PropTypes.string, // For preference persistence
};

export default ReasoningBlock;
